import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import ModalShell, { ModalFooter } from '../common/ModalShell';

// The small modal the Programme card's three actions share (Start, Tick with
// a note, Close): a header, a body, Cancel + a primary button. Escape and the
// backdrop close it unless a request is running.
function ProgrammeDialog({
    // `icon` and `danger` are still accepted from callers but no longer drawn:
    // the shared frame (common/ModalShell) has one title look and one teal
    // primary for every modal (owner, 16 Oct 2026).
    isOpen, title, testId, onClose, onSubmit, submitLabel, busy = false, submitDisabled = false,
    children,
}) {
    const { t } = useTranslation();
    const onCloseRef = useRef(onClose);
    useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
    const busyRef = useRef(busy);
    useEffect(() => { busyRef.current = busy; }, [busy]);

    useEffect(() => {
        if (!isOpen) return undefined;
        const onKey = (e) => { if (e.key === 'Escape' && !busyRef.current) onCloseRef.current?.(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isOpen]);

    if (!isOpen || typeof document === 'undefined') return null;
    const close = () => { if (!busy) onClose?.(); };

    return createPortal(
        // Click on the dimmed page closes (unless busy), as before the shared frame.
        <div onClick={(e) => { if (e.target === e.currentTarget.firstElementChild) close(); }}>
            <ModalShell
                title={title}
                onClose={close}
                closeDisabled={busy}
                closeLabel={t('programmes.close')}
                size="lg"
                zClass="z-[70]"
                contentTestId={testId}
                closeTestId={`${testId}-close`}
                bodyClassName="space-y-4"
                footer={(
                    <ModalFooter
                        onCancel={close}
                        cancelLabel={t('programmes.cancel')}
                        onSubmit={onSubmit}
                        submitLabel={submitLabel}
                        busy={busy}
                        disabled={submitDisabled}
                        cancelTestId={`${testId}-cancel`}
                        submitTestId={`${testId}-submit`}
                    />
                )}
            >
                {children}
            </ModalShell>
        </div>,
        document.body
    );
}

export default ProgrammeDialog;
